<template>
    <!-- Display a data table showing team leads and their associated project counts -->
    <v-data-table
        :headers="headers"
        :items="teamLeadsWithProjectCount"
        class="elevation-2 team-leaders-table"
        style="max-height: 100%; overflow-y: auto; scrollbar-width: none"
        :hide-default-footer="isNrOfItems"
        fixed-header
        items-per-page="-1"
        :sort-by="sortByConfig"
    >
        <!-- Custom header for Name column with sorting menu -->
        <template #header.name="{ column }">
            <div
                class="d-flex align-center justify-start flex-nowrap table-header-controls"
            >
                <span class="flex-shrink-0">{{ column.title }}</span>
                <TableSortMenu
                    v-model="sortOrder"
                    :show-custom-option="false"
                    @click.stop
                />
                <TableHeaderSearch
                    v-model="searchQuery"
                    v-model:open="searchOpen"
                />
            </div>
        </template>

        <!-- Only the name navigates; project count is for the tooltip -->
        <template #item.name="{ item }">
            <span
                class="user-name-link"
                @click.stop="openUserInfo(item)"
            >
                {{ item.name }} {{ item.surname }}
            </span>
        </template>

        <!-- Same project-count tooltip pattern as Statistics "By User" -->
        <template #item.projectCount="{ item }">
            <CountWithNamesTooltip
                :count="item.projectCount"
                :names="item.projectNames"
            />
        </template>
    </v-data-table>
</template>

<script>
import { mapGetters } from "vuex";
import { isBelowThreshold, navigateToUserInfo } from "@/utils/utils";
import {
    isProjectTeamLead,
    isUserActive,
    formatProjectLabel,
    compareActiveProjectsFirst,
} from "@/utils/projectUtils";
import TableSortMenu from "./TableSortMenu.vue";
import TableHeaderSearch from "./TableHeaderSearch.vue";
import CountWithNamesTooltip from "./CountWithNamesTooltip.vue";

export default {
    components: {
        TableSortMenu,
        TableHeaderSearch,
        CountWithNamesTooltip,
    },
    /**
     * Reactive data properties for the component.
     * @returns {Object} An object containing the headers for the data table.
     */
    data() {
        return {
            /**
             * Table headers for the team leads and their project counts.
             * @type {Array<{title: string, value: string}>}
             */
            headers: [
                { title: "Name", value: "name", width: "70%", sortable: true },
                { title: "Projects", value: "projectCount", width: "30%" },
            ],
            /**
             * Current sort order: 'A-Z' or 'Z-A'.
             * @type {String}
             */
            sortOrder: "A-Z",
            searchQuery: "",
            searchOpen: false,
        };
    },

    computed: {
        isNrOfItems() {
            return isBelowThreshold(this.teamLeadsWithProjectCount);
        },
        /**
         * Computed properties derived from Vuex store or internal data.
         */
        ...mapGetters([
            "getUsers", // Retrieves all users, including team leads.
            "getManagedProjects", // Retrieves all managed projects.
        ]),

        /**
         * Determines the sort-by configuration for Vuetify data table.
         * @returns {Array<Object>} Sort configuration.
         */
        sortByConfig() {
            if (this.sortOrder === "A-Z") {
                return [{ key: "name", order: "asc" }];
            } else if (this.sortOrder === "Z-A") {
                return [{ key: "name", order: "desc" }];
            }
            // Default to ascending
            return [{ key: "name", order: "asc" }];
        },

        /**
         * Team leads + project counts (no text search).
         */
        teamLeadsWithProjectCountBase() {
            const projects = this.getManagedProjects || [];
            const users = this.getUsers || [];

            return users
                .filter((user) => isUserActive(user))
                .map((user) => {
                    const isTeamLead = user.role === "teamLead";
                    const isAdminWithProjects = user.role === "admin";

                    const projectNames = projects
                        .filter((project) =>
                            isProjectTeamLead(project, user.uid)
                        )
                        .sort((a, b) => {
                            const byActive = compareActiveProjectsFirst(a, b);
                            if (byActive !== 0) return byActive;
                            return String(a.name || "").localeCompare(
                                String(b.name || "")
                            );
                        })
                        .map((project) => formatProjectLabel(project));
                    const projectCount = projectNames.length;

                    if (
                        isTeamLead ||
                        (isAdminWithProjects && projectCount > 0)
                    ) {
                        return {
                            uid: user.uid,
                            name: user.name || user.email,
                            surname: user.surname,
                            projectCount,
                            projectNames,
                        };
                    }
                    return null;
                })
                .filter((user) => user !== null);
        },

        /**
         * Table items: base list filtered by header search only.
         */
        teamLeadsWithProjectCount() {
            const q = (this.searchQuery || "").trim().toLowerCase();
            const result = this.teamLeadsWithProjectCountBase;
            if (!q) return result;
            return result.filter((user) => {
                const fullName = `${user.name || ""} ${user.surname || ""}`
                    .trim()
                    .toLowerCase();
                return fullName.includes(q);
            });
        },
    },
    methods: {
        openUserInfo(item) {
            navigateToUserInfo(this.$router, item?.uid);
        },
    },
};
</script>

<style scoped>
.table-header-controls {
    position: relative;
    overflow: visible;
}

:deep(th) {
    overflow: visible;
}

.user-name-link {
    cursor: pointer;
    color: inherit;
}
.user-name-link:hover {
    text-decoration: underline;
}
</style>
